import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, type Profile, type Appointment } from '@/lib/supabase';

export default function DashboardPage() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [clientCount, setClientCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'upcoming' | 'past'>('upcoming');

  const loadProfile = useCallback(async (userId: string) => {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .maybeSingle();

    if (error) {
      console.error('Profile load error:', error);
      return;
    }
    if (data) setProfile(data);
  }, []);

  const loadAppointments = useCallback(async () => {
    const { data, error } = await supabase
      .from('appointments')
      .select('*, client:clients(*)')
      .order('start_time', { ascending: true });

    if (error) {
      console.error('Appointments load error:', error);
      return;
    }
    setAppointments(data || []);
  }, []);

  const loadClientCount = useCallback(async () => {
    const { count, error } = await supabase
      .from('clients')
      .select('*', { count: 'exact', head: true });

    if (!error && count !== null) setClientCount(count);
  }, []);

  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      const { data: { user: authUser } } = await supabase.auth.getUser();

      if (!authUser) {
        navigate('/login');
        return;
      }

      await Promise.all([
        loadProfile(authUser.id),
        loadAppointments(),
        loadClientCount(),
      ]);
      setIsLoading(false);
    };

    init();
  }, [navigate, loadProfile, loadAppointments, loadClientCount]);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  const now = new Date();
  const upcomingAppointments = appointments.filter((apt) => new Date(apt.start_time) >= now);
  const pastAppointments = appointments.filter((apt) => new Date(apt.start_time) < now);
  const displayedAppointments = activeTab === 'upcoming' ? upcomingAppointments : pastAppointments;

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

  const formatTime = (dateString: string) =>
    new Date(dateString).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });

  const handleDeleteAppointment = async (id: string) => {
    const { error } = await supabase.from('appointments').delete().eq('id', id);
    if (!error) {
      setAppointments((prev) => prev.filter((a) => a.id !== id));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200 h-16">
        <div className="max-w-7xl mx-auto px-6 h-full flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-2xl font-bold text-gray-900">Appofy</span>
          </div>
          <div className="flex items-center gap-6">
            <button onClick={() => navigate('/dashboard')} className="text-teal-600 font-semibold cursor-pointer whitespace-nowrap">
              Dashboard
            </button>
            <button onClick={() => navigate('/calendar')} className="text-gray-600 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap">
              Calendar
            </button>
            <button onClick={() => navigate('/clients')} className="text-gray-600 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap">
              Clients
            </button>
            <div className="flex items-center gap-3 pl-6 border-l border-gray-200">
              <div className="text-right">
                <div className="text-sm font-semibold text-gray-900">{profile?.full_name || 'User'}</div>
                <div className="text-xs text-gray-500">{profile?.business_name || 'Business Owner'}</div>
              </div>
              <button
                onClick={handleSignOut}
                className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center hover:bg-gray-200 transition-colors cursor-pointer"
                title="Sign out"
              >
                <i className="ri-logout-box-line text-gray-600"></i>
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-1">Dashboard</h1>
          <p className="text-gray-600">Welcome back{profile?.full_name ? `, ${profile.full_name}` : ''}!</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl p-5 border border-gray-200">
            <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center mb-3">
              <i className="ri-calendar-check-line text-xl text-teal-600"></i>
            </div>
            <div className="text-2xl font-bold text-gray-900">{upcomingAppointments.length}</div>
            <div className="text-sm text-gray-500">Upcoming</div>
          </div>

          <div className="bg-white rounded-xl p-5 border border-gray-200">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center mb-3">
              <i className="ri-history-line text-xl text-orange-600"></i>
            </div>
            <div className="text-2xl font-bold text-gray-900">{pastAppointments.length}</div>
            <div className="text-sm text-gray-500">Completed</div>
          </div>

          <div className="bg-white rounded-xl p-5 border border-gray-200">
            <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mb-3">
              <i className="ri-user-line text-xl text-gray-600"></i>
            </div>
            <div className="text-2xl font-bold text-gray-900">{clientCount}</div>
            <div className="text-sm text-gray-500">Total Clients</div>
          </div>

          <div
            onClick={() => navigate('/calendar')}
            className="bg-teal-500 rounded-xl p-5 flex flex-col items-center justify-center cursor-pointer hover:bg-teal-600 transition-colors"
          >
            <i className="ri-add-circle-line text-3xl text-white mb-1"></i>
            <div className="text-sm font-semibold text-white whitespace-nowrap">New Appointment</div>
          </div>
        </div>

        {/* Appointments List */}
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="p-6 border-b border-gray-100 flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">Appointments</h2>
            <div className="inline-flex items-center gap-1 bg-gray-100 p-1 rounded-lg">
              <button
                onClick={() => setActiveTab('upcoming')}
                className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-all whitespace-nowrap cursor-pointer ${activeTab === 'upcoming' ? 'bg-white text-gray-900' : 'text-gray-500'}`}
              >
                Upcoming
              </button>
              <button
                onClick={() => setActiveTab('past')}
                className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-all whitespace-nowrap cursor-pointer ${activeTab === 'past' ? 'bg-white text-gray-900' : 'text-gray-500'}`}
              >
                Past
              </button>
            </div>
          </div>

          <div className="p-6">
            {isLoading ? (
              <div className="text-center py-12">
                <i className="ri-loader-4-line text-4xl text-gray-400 animate-spin"></i>
                <p className="text-gray-500 mt-3 text-sm">Loading...</p>
              </div>
            ) : displayedAppointments.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-calendar-line text-3xl text-gray-400"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">No {activeTab} appointments</h3>
                <p className="text-gray-500 text-sm mb-5">Get started by creating your first appointment</p>
                <button
                  onClick={() => navigate('/calendar')}
                  className="bg-teal-500 text-white px-5 py-2.5 rounded-lg hover:bg-teal-600 transition-all text-sm font-semibold whitespace-nowrap cursor-pointer"
                >
                  Create Appointment
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {displayedAppointments.map((appointment) => (
                  <div
                    key={appointment.id}
                    className="flex items-center gap-4 p-4 border border-gray-200 rounded-xl hover:border-teal-400 transition-all"
                  >
                    <div
                      className="w-1.5 h-14 rounded-full flex-shrink-0"
                      style={{ backgroundColor: appointment.color || '#14B8A6' }}
                    />
                    <div className="flex-1 min-w-0">
                      <h4 className="font-bold text-gray-900 mb-0.5 truncate">{appointment.title}</h4>
                      {appointment.description && (
                        <p className="text-gray-500 text-sm mb-1 truncate">{appointment.description}</p>
                      )}
                      <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <i className="ri-calendar-line"></i>
                          {formatDate(appointment.start_time)}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-time-line"></i>
                          {formatTime(appointment.start_time)} – {formatTime(appointment.end_time)}
                        </span>
                        {appointment.client && (
                          <span className="flex items-center gap-1">
                            <i className="ri-user-line"></i>
                            {appointment.client.full_name}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span
                        className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                          appointment.status === 'scheduled'
                            ? 'bg-teal-100 text-teal-700'
                            : appointment.status === 'completed'
                            ? 'bg-gray-100 text-gray-700'
                            : 'bg-orange-100 text-orange-700'
                        }`}
                      >
                        {appointment.status}
                      </span>
                      <button
                        onClick={() => handleDeleteAppointment(appointment.id)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors cursor-pointer"
                        title="Delete"
                      >
                        <i className="ri-delete-bin-line text-sm"></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
